import React from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore, getDefaultRouteForUser } from '@/stores/authStore'

// Layout Components
import AuthLayout from './layouts/AuthLayout'
import ProtectedLayout from './ProtectedLayout'
import AuthLoadingScreen from './AuthLoadingScreen'

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import SignupPage from '@/pages/auth/SignupPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'

// Dashboard Pages
import Dashboard from '@/pages/dashboard/Dashboard'
import WorkoutPlanner from '@/pages/workout/WorkoutPlanner'
import ProgressTracker from '@/pages/progress/ProgressTracker'
import Reports from '@/pages/reports/Reports'
import Profile from '@/pages/profile/Profile'
import Settings from '@/pages/settings/Settings'
import Billing from '@/pages/billing/Billing'
import BillingSuccess from '@/pages/billing/BillingSuccess'
import BillingCancel from '@/pages/billing/BillingCancel'

// Landing Page
import LandingPage from '@/pages/LandingPage'

// Client Components
import ClientComposition from '@/pages/client/ClientComposition'
import ClientNutrition from '@/pages/client/ClientNutrition'
import ClientExercise from '@/pages/client/ClientExercise'
import ClientResources from '@/pages/client/ClientResources'
import ClientProgramArchive from '@/pages/client/ClientProgramArchive'
import ProgramBinder from '@/pages/client/ProgramBinder'

// Consultant Components
import ConsultantClients from '@/pages/consultant/ConsultantClients'
import ConsultantClientDetail from '@/pages/consultant/ConsultantClientDetail'
import ConsultantProgramDetail from '@/pages/consultant/ConsultantProgramDetail'
import SessionDetail from '@/pages/consultant/SessionDetail'
import ExercisePlanBuilder from '@/pages/consultant/ExercisePlanBuilder'
import ConsultantNotes from '@/pages/consultant/ConsultantNotes'

// Admin Components
import Resources from '@/pages/admin/Resources'
import Uploader from '@/pages/admin/Uploader'
import Prospects from '@/pages/admin/Prospects'
import PlatformSetup from '@/pages/admin/PlatformSetup'
import CompanyClients from '@/pages/admin/CompanyClients'
import Notes from '@/pages/admin/Notes'
import UserUpload from '@/pages/admin/UserUpload'
import SiteSettings from '@/pages/admin/SiteSettings'
import CompanySettings from '@/pages/admin/CompanySettings'
import RolesEditor from '@/pages/admin/RolesEditor'

const AppRoutes = () => {
  const { isAuthenticated, isAuthenticating, user } = useAuthStore()

  const roleSource = user?.role || user?.user_type
  const isClient = typeof roleSource === 'string' && roleSource.toLowerCase() === 'client'
  const isSuperuser = user?.role?.id === 1 || user?.role?.name === 'Superuser'
  const isCompanyAdmin = user?.role?.name === 'Company Admin'

  // Default landing route after login based on role
  const defaultRoute = getDefaultRouteForUser(user)

  // Show loading screen during authentication process
  if (isAuthenticating) {
    return <AuthLoadingScreen />
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={isAuthenticated ? <Navigate to={defaultRoute} replace /> : <LandingPage />} />

      {/* Auth Routes */}
      <Route path="/auth" element={<AuthLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route path="forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* Redirect authenticated users from auth pages */}
      {isAuthenticated && (
        <Route path="/auth/*" element={<Navigate to={defaultRoute} replace />} />
      )}

      {/* Protected Routes - All use single ProtectedLayout */}
      <Route path="/*" element={<ProtectedLayout />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="workouts" element={<WorkoutPlanner />} />
        {/* <Route path="nutrition" element={<ClientNutrition />} /> */}
        <Route path="progress" element={<ProgressTracker />} />
        <Route path="reports" element={<Reports />} />
        <Route path="profile" element={<Profile />} />
        <Route path="settings" element={<Settings />} />
        <Route path="billing" element={<Billing />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="billing/cancel" element={<BillingCancel />} />
        
        {/* Client Routes */}
        <Route path="clientcomposition" element={<ClientComposition />} />
        <Route path="clientnutrition" element={<ClientNutrition />} />
        <Route path="clientexercise" element={<ClientExercise />} />
        <Route path="clientprogramarchive" element={<ClientProgramArchive />} />
        <Route path="program-binder" element={<ProgramBinder />} />
        
        {/* Consultant Routes */}
        <Route path="consultant-clients" element={<ConsultantClients />} />
        <Route path="consultant-clients/:clientId" element={<ConsultantClientDetail />} />
        <Route path="consultant-clients/:clientId/programs/:programId" element={<ConsultantProgramDetail />} />
        <Route path="consultant-clients/:clientId/sessions/:sessionId" element={<SessionDetail />} />
        <Route path="exercise-plan-builder" element={<ExercisePlanBuilder />} />
        <Route path="consultant-notes" element={<ConsultantNotes />} />
        
        {/* Admin Routes */}
        <Route path="resources" element={isClient ? <ClientResources /> : <ClientResources />} />
        <Route path="uploader" element={<Uploader />} />
        <Route path="prospects" element={<Prospects />} />
        <Route path="platform-setup" element={(isSuperuser || isCompanyAdmin) ? <PlatformSetup /> : <Navigate to="/dashboard" replace />} />
        <Route path="company-clients" element={<CompanyClients />} />
        <Route path="company-clients/:clientId" element={<ConsultantClientDetail />} />
        <Route path="company-clients/:clientId/programs/:programId" element={<ConsultantProgramDetail />} />
        <Route path="company-clients/:clientId/sessions/:sessionId" element={<SessionDetail />} />
        <Route path="notes" element={<Notes />} />
        <Route path="site-settings" element={isSuperuser ? <SiteSettings /> : <Navigate to="/platform-setup" replace />} />
        <Route path="company-settings" element={<CompanySettings />} />
        <Route path="roles-editor" element={<RolesEditor />} />
      </Route>
    </Routes>
  )
}

export default AppRoutes
